import type { ExportBundle } from "@wodiary/shared";

/**
 * 把导出产物变成可下载的文件。
 *
 * ⚠️ 下载在**浏览器**里组织：后端只返回一个 `ExportBundle`（JSON + Markdown
 * 两路），没有 zip 通道。所以：
 * - JSON：可重建的结构化副本，单独一个 `.json` 文件。
 * - Markdown：后端的「按年月分文件」在浏览器里合并成**一个** `.md`，每个月
 *   用一级标题分隔。Markdown 本来就是「能读不能恢复」的归档，合并不损失
 *   可读性；真要按月分文件，从 JSON 重新生成即可。
 */
function triggerDownload(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 立即 revoke 会让部分浏览器来不及开始下载。
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function stamp(iso: string): string {
  return iso.slice(0, 10);
}

export function downloadExportJson(bundle: ExportBundle): void {
  triggerDownload(
    `wodiary-${stamp(bundle.exportedAt)}.json`,
    JSON.stringify(bundle, null, 2),
    "application/json",
  );
}

export function downloadExportMarkdown(bundle: ExportBundle): void {
  // 后端每个文件的内容自带标题，这里只加一行注释标明它原本的文件名，避免
  // 叠出两个一级标题。
  const parts = bundle.markdown.map(
    (file) => `<!-- ${file.filename} -->\n\n${file.content}`,
  );
  const header = `<!-- 我们日记导出 · ${bundle.exportedAt} · ${bundle.entries.length} 篇 -->\n\n`;
  triggerDownload(
    `wodiary-${stamp(bundle.exportedAt)}.md`,
    header + parts.join("\n\n---\n\n"),
    "text/markdown",
  );
}
